import { Button, Dialog, DialogActions, DialogContent, Stack, TextField } from '@mui/material';
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from 'formik';
import { useSnackbar } from 'notistack';
import React from 'react';

import { OrganizationViewModel, useCreateOrganization, useUpdateOrganization } from '@/api';
import { UIDialogTitle } from '@/shared/components';
import { useMedia } from '@/shared/hooks';

import { OrganizationFormValues, validationSchema } from './duck';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  selectedOrganization?: OrganizationViewModel;
  setSelectedOrganization?: React.Dispatch<React.SetStateAction<OrganizationViewModel | undefined>>;
}

const CreateEditModal: React.FC<Props> = ({
  isOpen,
  onClose,
  selectedOrganization,
  setSelectedOrganization,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { isSmallScreen } = useMedia();
  const { createOrganization, isCreating } = useCreateOrganization();
  const { updateOrganization, isUpdating } = useUpdateOrganization();

  const initialFormValues: OrganizationFormValues = React.useMemo(() => {
    return {
      id: selectedOrganization?.id || '',
      name: selectedOrganization?.name || '',
      domain: selectedOrganization?.domain || '',
    };
  }, [selectedOrganization]);

  const handleClose = () => {
    if (selectedOrganization && setSelectedOrganization) {
      setSelectedOrganization(undefined);
    }
    onClose();
  };

  const handleSubmit = async (values: OrganizationFormValues, { resetForm }: FormikHelpers<OrganizationFormValues>) => {
    try {
      if (selectedOrganization) {
        await updateOrganization({ id: values.id || '', data: values });
      } else {
        await createOrganization(values);
      }
      enqueueSnackbar({
        message: `Organization ${selectedOrganization ? 'updated' : 'created'} successfully!`,
        variant: 'success',
      });
      resetForm();
      handleClose();
    } catch (error) {
      enqueueSnackbar({
        message: error instanceof Error ? error.message : `An error occurred while ${selectedOrganization
          ? 'updating'
          : 'creating'} the organization.`,
        variant: 'error',
      });
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullScreen={isSmallScreen}
      fullWidth
      maxWidth='sm'
      scroll='paper'
      aria-labelledby='dialog-title'
      aria-describedby='dialog-content'
    >
      <UIDialogTitle id='dialog-title' title={`${selectedOrganization ? 'Edit' : 'New'} Organization`} onCloseBtnAction={handleClose} />

      <Formik<OrganizationFormValues>
        initialValues={initialFormValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange
        enableReinitialize
      >
        {({ isSubmitting, errors, touched, resetForm }) => {
          return (
            <Form>
              <DialogContent id='dialog-content'>
                <Stack
                  gap='2rem'
                  flexDirection='column'
                  width='100%'
                >
                  <Field
                    as={TextField}
                    name='name'
                    required
                    fullWidth
                    type='text'
                    label='Organization name'
                    variant='outlined'
                    helperText={<ErrorMessage name='name' />}
                    error={Boolean(touched.name && errors.name)}
                    disabled={isSubmitting || isCreating || isUpdating}
                  />

                  <Field
                    as={TextField}
                    name='domain'
                    required
                    fullWidth
                    type='text'
                    label='Domain'
                    variant='outlined'
                    helperText={<ErrorMessage name='domain' />}
                    error={Boolean(touched.domain && errors.domain)}
                    disabled={isSubmitting || isCreating || isUpdating}
                  />
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button
                  variant='outlined'
                  color='secondary'
                  onClick={() => {
                    resetForm();
                    handleClose();
                  }}
                  disabled={isSubmitting || isCreating || isUpdating}
                >
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  type='submit'
                  disabled={isSubmitting || isCreating || isUpdating}
                >
                  Save
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default CreateEditModal;